import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {
    PhosphorIcon,
    type PhosphorIconAsset,
} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {ImageSource} from "@khan/guide-types";

import camera from "@phosphor-icons/core/regular/camera.svg";
import images from "@phosphor-icons/core/regular/images.svg";
import link from "@phosphor-icons/core/regular/link.svg";
import paperclip from "@phosphor-icons/core/regular/paperclip.svg";

export interface ImageInputCustomOptions {
    labels?: Partial<Record<ImageSource, string>>;
    icons?: Partial<Record<ImageSource, PhosphorIconAsset>>;
}

interface Props {
    // Callback to open the modal for the selected image source.
    onOpenModal: (imageSource: ImageSource) => void;
    // Optional custom opener button. If not provided, uses default paperclip button.
    opener?: () => React.ReactElement;
    // Optional custom dropdown styling. If not provided, uses default styling.
    dropdownStyle?: React.CSSProperties;
    // Optional custom labels and icons. If not provided, uses defaults.
    customOptions?: ImageInputCustomOptions;
}

// Map of image sources to their corresponding icons.
const defaultIcons = {
    [ImageSource.ProvidedByFile]: images,
    [ImageSource.ProvidedByCamera]: camera,
    [ImageSource.ProvidedByURL]: link,
};

/**
 * A dropdown menu for selecting the image input mode (file upload, camera, or URL).
 */
export const InputModeMenu = ({
    onOpenModal,
    opener,
    dropdownStyle,
    customOptions,
}: Props) => {
    const label = t`Select an image input mode`;

    // Use custom labels if provided, otherwise use defaults
    const defaultLabels = {
        [ImageSource.ProvidedByFile]: t`Images`,
        [ImageSource.ProvidedByCamera]: t`Camera`,
        [ImageSource.ProvidedByURL]: t`From URL`,
    };
    const labels = {...defaultLabels, ...customOptions?.labels};

    // Use custom icons if provided, otherwise use defaults
    const iconsToUse = {...defaultIcons, ...customOptions?.icons};

    const options = [
        {
            label: labels[ImageSource.ProvidedByFile],
            value: ImageSource.ProvidedByFile,
        },
        {
            label: labels[ImageSource.ProvidedByCamera],
            value: ImageSource.ProvidedByCamera,
        },
        {
            label: labels[ImageSource.ProvidedByURL],
            value: ImageSource.ProvidedByURL,
        },
    ];

    const defaultOpener = () => (
        <IconButton
            size="small"
            style={styles.attachButton}
            kind="tertiary"
            icon={paperclip}
            aria-label={label}
            testId="image-attach-button"
        />
    );

    // Use custom dropdown style if provided, otherwise use default
    const defaultDropdownStyle = {transform: "translateY(-64px)"};
    const finalDropdownStyle = dropdownStyle || defaultDropdownStyle;

    return (
        <ActionMenu
            // Move the menu above the tooltip.
            dropdownStyle={finalDropdownStyle}
            aria-label={label}
            menuText=""
            opener={opener || defaultOpener}
        >
            {options.map(({label, value}) => (
                <ActionItem
                    key={value}
                    label={label}
                    leftAccessory={
                        <PhosphorIcon
                            size="medium"
                            icon={iconsToUse[value] || defaultIcons[value]}
                        />
                    }
                    onClick={() => onOpenModal(value)}
                />
            ))}
        </ActionMenu>
    );
};

const styles = StyleSheet.create({
    attachButton: {
        color: color.offBlack64,
        ":hover": {
            color: color.offBlack64,
            borderColor: color.offBlack64,
        },
    },
});
